// External Imports
import { Fragment, useContext, useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  MdCheck,
  MdClose,
  MdEdit,
  MdPayments,
  MdRefresh,
} from "react-icons/md";

// Internal Imports
import { requestAdminAPI } from "../../api/posh-api";
import { UtilityContext } from "../../context/util-context";
import { constructDateObject, isoDateToString } from "../../util/format-date";
import DetailPair from "../UI/DetailPair";
import Heading from "../UI/Heading";
import DropDownBanner from "../UI/DropDownBanner";
import LinkPair from "../UI/LinkPair";
import uploadFile from "../../util/upload-file";
import { validateImageFile } from "../../util/validate-file";
import Notes from "../UI/Notes";
import DetailList from "../UI/DetailList";
import CustomerScore from "./CustomerScore";
import CustomerButtons from "./CustomerButtons";
import CustomerBookings from "./CustomerBookings";
import CustomerScreenings from "./CustomerScreenings";
import PaymentMethods from "./PaymentMethods";
import PersonCard from "../UI/PersonCard";
import ImageSelector from "../UI/ImageSelector";
import PlanName from "../UI/PlanName";
import { DataContext } from "../../context/data-context";
import EmployeeSelector from "../employees-page/EmployeeSelector";
import LicenseStatusModal from "./LicenseStatusModal";
import TagSelector from "../UI/TagSelector";
import AuditLogs from "../UI/AuditLogs";

/** ========================================================================
 * * Customer Information Page
 * Renders a page, which displays all information about a customer.
 * ========================================================================== */

export default function CustomerInfo() {
  // Load Context
  const { navigate } = useContext(UtilityContext);
  const { admin } = useContext(DataContext);

  // Get Customer & Session ID from URL
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  // Initialize States
  const [customer, setCustomer] = useState({});
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);
  const [showScreenings, setShowScreenings] = useState(false);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);

  /* =================== FETCH CUSTOMER ===================*/
  const fetchCustomer = useCallback(async () => {
    try {
      setLoading(true);
      const data = await requestAdminAPI("fetch", {
        table: "customer",
        id,
        columns: [
          "booking: activeBooking { id vehicle { id make { name } model year vin cornerImage } startDate endDate}",
          "bookings: bookings_on_customer { dateCreated id vehicle { model year vin } startDate endDate totalFees }",
          "defaultPaymentMethod { brand last4 id }",
          "documents: documents_on_customer { link type expDate }",
          "payments: payments_on_customer { datePaid dueDate type amount status }",
          "paymentMethods: paymentMethods_on_customer { id brand last4 type dateCreated accountId deleted }",
          "screenings: customerScreenings_on_customer { provider id status link phone kyc risk document dateCreated dateCompleted }",
          "plan: customerPlan_on_customer { plan { name } rate dateCreated }",
          "advisor { name type photo }",
        ],
      });

      setCustomer(data);
      setLoading(false);
    } catch (err) {
      setLoading(false);

      console.error(err);
    }
  }, [id]);

  /* =================== EDIT CUSTOMER =================== */
  const editHandler = async () => {
    try {
      const payload = {
        customerId: id,
        phone: document.getElementById("phone").value,
        dob: document.getElementById("dob").value,
        address: document.getElementById("address").value,
        driversLicenseNumber: document.getElementById("number").value,
        driversLicenseExp: document.getElementById("exp").value,
        insuranceExp: document.getElementById("insuranceExp").value,
      };
      if (payload.dob)
        payload.dob = constructDateObject(payload.dob, "12:00:00");
      if (payload.driversLicenseExp)
        payload.driversLicenseExp = constructDateObject(
          payload.driversLicenseExp,
          "12:00:00",
        );
      if (payload.insuranceExp)
        payload.insuranceExp = constructDateObject(
          payload.insuranceExp,
          "12:00:00",
        );

      const licenseFrontFile =
        document.getElementById("licenseFront")?.files[0];
      const licensebackFile = document.getElementById("licenseBack")?.files[0];
      const documentFile = document.getElementById("document").files[0];

      // Validate license image files
      const frontValidation = validateImageFile(licenseFrontFile, "License Front");
      if (!frontValidation.isValid) {
        alert(frontValidation.error);
        setLoading(false);
        return;
      }
      const backValidation = validateImageFile(licensebackFile, "License Back");
      if (!backValidation.isValid) {
        alert(backValidation.error);
        setLoading(false);
        return;
      }

      setLoading(true);

      // Upload License Front
      if (licenseFrontFile) {
        const path = `users/${customer.id}/license_front.${licenseFrontFile.name
          .split(".")
          .pop()}`;
        payload.driversLicenseFront = await uploadFile(path, licenseFrontFile);
      }

      // Upload License Back
      if (licensebackFile) {
        const path = `users/${customer.id}/license_back.${licensebackFile.name
          .split(".")
          .pop()}`;
        payload.driversLicenseBack = await uploadFile(path, licensebackFile);
      }

      // Upload Document
      if (documentFile) {
        const path = `users/${customer.id}/document.${documentFile.name
          .split(".")
          .pop()}`;
        payload.document = await uploadFile(path, documentFile);
      }

      // Send Request
      await requestAdminAPI("edit-customer", payload);

      // Refresh Page
      setEdit(false);
      fetchCustomer();
    } catch (err) {
      console.error(err.message);
      setLoading(false);
    }
  };

  /* =================== CLAIM CUSTOMER =================== */
  const claimClient = async () => {
    try {
      setLoading(true);
      await requestAdminAPI("edit-customer", {
        customerId: id,
        phone: customer.phone,
        advisorId: admin.id,
      });
      fetchCustomer();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* =================== UPDATE ADVISOR =================== */
  const updateAdvisor = async (employee) => {
    try {
      setLoading(true);
      await requestAdminAPI("update-advisor", {
        customerId: id,
        advisorId: employee.id,
      });
      setEdit(false);
      fetchCustomer();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Fetch Customer Information on Page Load
  useEffect(() => {
    fetchCustomer();
  }, []);

  if (!id || (!loading && (!customer || !customer?.id)))
    return (
      <div className="container">
        <p className="text-stone-400">
          Customer ID Not Provided or Customer Not Found!
        </p>
      </div>
    );
  return (
    <div className="divider">
      <CustomerScreenings
        screenings={customer.screenings}
        open={showScreenings}
        setOpen={setShowScreenings}
        customerId={customer.id}
        refresh={fetchCustomer}
      />
      <PaymentMethods
        methods={customer.paymentMethods}
        open={showPaymentMethods}
        setOpen={setShowPaymentMethods}
        defaultId={customer.defaultPaymentMethod?.id}
        refresh={fetchCustomer}
        customerId={customer.id}
      />
      <LicenseStatusModal
        open={showLicenseModal}
        setOpen={setShowLicenseModal}
        checkrId={customer.checkrId}
        customerId={customer.id}
      />
      <div className="container gap-10 text-sm lg:col-span-3">
        <Heading title="Customer Information" id={id}>
          <AuditLogs table="customer" id={id} />
          <MdRefresh onClick={fetchCustomer} className="icon" />
          {customer.doNotRent && (
            <p className="px-3 py-1 text-sm bg-red-700 rounded-md text-stone-200 animate-pulse">
              Do Not Rent!
            </p>
          )}
          {customer.deleted && (
            <p className="px-3 py-1 text-sm bg-red-700 rounded-md text-stone-200 animate-pulse">
              Deleted
            </p>
          )}
        </Heading>
        <div className="grid lg:grid-cols-3">
          <PersonCard
            label={customer.email}
            name={`${customer.firstName} ${customer.lastName}`}
            secondary={isoDateToString(customer.dateCreated)}
            image={customer.photo}
            loading={loading}
          />
          {customer.plan && (
            <div
              onClick={() =>
                document.getElementById("edit-subscription").click()
              }
              className="grid clickable w-fit"
            >
              <p className="text-stone-400">
                {isoDateToString(customer.plan?.dateCreated)}
              </p>
              <PlanName
                plan={customer.plan?.plan?.name}
                loading={loading}
                size="text-4xl"
              />
              <p className="text-stone-400">
                {customer.plan?.rate
                  ? `$${customer.plan?.rate}`
                  : "Free Subscription!"}
              </p>
            </div>
          )}

          {!loading && edit ? (
            <div>
              <EmployeeSelector label="Advisor" onSelect={updateAdvisor} />
            </div>
          ) : loading || customer.advisor ? (
            <PersonCard
              label="Advisor"
              name={customer.advisor?.name}
              secondary={customer.advisor?.type}
              image={customer.advisor?.photo}
              loading={loading}
              link={`/employees/info?id=${customer.advisorId}`}
            />
          ) : (
            <div onClick={claimClient} className="my-auto btn-secondary">
              Claim Client
            </div>
          )}
        </div>
        <div className="info">
          <DetailPair
            label="Phone"
            value={customer.phone}
            edit={edit}
            loading={loading}
            inputOptions={{ type: "tel", placeholder: "e.g., +17812491152" }}
          />

          <DetailPair
            label="DOB"
            edit={edit}
            loading={loading}
            value={isoDateToString(customer.dob)}
            inputOptions={{
              type: "date",
              placeholder: "MM/DD/YYYY",
              defaultValue: isoDateToString(customer.dob, "yyyy-MM-dd"),
            }}
          />
          <DetailPair
            label="Default Payment"
            onClick={() => setShowPaymentMethods(true)}
            value={
              customer.defaultPaymentMethod &&
              `${customer.defaultPaymentMethod.brand} ${customer.defaultPaymentMethod.last4}`
            }
            loading={loading}
          />
          <DetailPair
            label="License Status"
            value={customer.driversLicenseStatus}
            loading={loading}
            onClick={() => setShowLicenseModal(true)}
            classes={`${
              customer.driversLicenseStatus === "VALID"
                ? "text-green-600"
                : customer.driversLicenseStatus === "SUSPENDED"
                  ? "text-red-600"
                  : "text-yellow-600"
            } font-semibold`}
          />
          <LinkPair
            label="License Front"
            link={customer.driversLicenseFront}
            loading={loading}
            edit={edit}
            inputOptions={{ 
              type: "file", 
              id: "licenseFront",
              accept: ".png,.webp,.PNG,.WEBP"
            }}
          />
          <LinkPair
            label="License Back"
            link={customer.driversLicenseBack}
            loading={loading}
            edit={edit}
            inputOptions={{ 
              type: "file", 
              id: "licenseBack",
              accept: ".png,.webp,.PNG,.WEBP"
            }}
          />
          <DetailPair
            label="License Number"
            value={customer.driversLicenseNumber}
            loading={loading}
            edit={edit}
            inputOptions={{
              type: "text",
              id: "number",
              defaultValue: customer.driversLicenseNumber,
            }}
          />
          <DetailPair
            label="License Expiration"
            value={isoDateToString(customer.driversLicenseExp)}
            edit={edit}
            loading={loading}
            inputOptions={{
              type: "date",
              placeholder: "MM/DD/YYYY",
              id: "exp",
              defaultValue: isoDateToString(
                customer.driversLicenseExp,
                "yyyy-MM-dd",
              ),
            }}
          />
          {edit ? (
            <div className="lg:col-span-2">
              <DetailPair
                loading={loading}
                label="Address"
                value={customer.address}
                edit={edit}
                inputOptions={{ type: "text", id: "address" }}
              />
            </div>
          ) : (
            <Fragment>
              <DetailPair
                label="Street"
                value={customer.street}
                loading={loading}
              />
              <DetailPair
                label="City"
                value={customer.city}
                loading={loading}
              />
              <DetailPair
                label="State"
                value={customer.state}
                loading={loading}
              />
              <DetailPair label="Zip" value={customer.zip} loading={loading} />
            </Fragment>
          )}
          {edit ? (
            <LinkPair
              label="Document"
              edit={true}
              inputOptions={{ type: "file", id: "document" }}
              loading={loading}
            />
          ) : (
            <DropDownBanner data={customer.documents}>
              <DetailPair
                label="Documents"
                value={customer.documents?.length}
                loading={loading}
              />
            </DropDownBanner>
          )}
          <DetailPair
            label="Insurance Expiration"
            value={isoDateToString(customer.insuranceExp)}
            edit={edit}
            inputOptions={{
              type: "date",
              defaultValue: isoDateToString(
                customer.insuranceExp,
                "yyyy-MM-dd",
              ),
              id: "insuranceExp",
            }}
            loading={loading}
          />
          <DetailPair
            label="Screening Status"
            value={customer.screeningStatus?.toUpperCase()}
            classes={
              !customer.screeningStatus
                ? "text-stone-400"
                : customer.screeningStatus === "approved"
                  ? "text-green-600 font-semibold"
                  : customer.screeningStatus === "declined"
                    ? "text-red-600 font-semibold"
                    : "text-yellow-500"
            }
            onClick={() => setShowScreenings(true)}
            loading={loading}
          />
          <DetailPair
            label="Unpaid Invoices"
            classes={
              customer.unpaidInvoices
                ? "text-red-600 font-semibold animate-pulse"
                : ""
            }
            value={customer.unpaidInvoices ? "Yes" : "No"}
            link={`/customers/payments?id=${customer.id}&returnTo=/customers/info?id=${customer.id}`}
            loading={loading}
          />
        </div>
        <CustomerBookings bookings={customer.bookings} />
        <Notes table="customer" id={customer.id} />
      </div>
      <div className="sidebar">
        <div className="grid gap-4 overflow-y-scroll no-scrollbar">
          <div className="flex items-center justify-around space-x-4">
            {edit ? (
              <Fragment>
                <MdClose onClick={() => setEdit(false)} className="icon" />
                <MdCheck onClick={editHandler} className="icon" />
              </Fragment>
            ) : (
              <MdEdit onClick={() => setEdit(true)} className="icon" />
            )}
            <MdPayments
              onClick={() =>
                navigate(
                  `/customers/payments?id=${customer.id}&returnTo=/customers/info?id=${customer.id}`
                )
              }
              className="icon"
            />
          </div>
          <CustomerScore customer={customer} loading={loading} />
          {customer.booking && (
            <div
              onClick={(e) =>
                navigate(`/vehicles/info?id=${customer.booking?.vehicle.id}`, e)
              }
              className="clickable"
            >
              <ImageSelector
                image={customer.booking?.vehicle?.cornerImage}
                classes="w-full"
                loading={loading}
              />
            </div>
          )}
          {customer.booking && (
            <DetailList
              label="Booking Details"
              values={[
                `${isoDateToString(
                  customer.booking.startDate,
                )} - ${isoDateToString(customer.booking.endDate)}`,
                `${customer.booking.vehicle.year} ${customer.booking.vehicle.make.name} ${customer.booking.vehicle.model}`,
                customer.booking.vehicle.vin,
              ]}
              link={`/bookings/info?id=${customer.booking.id}`}
            />
          )}
          <TagSelector table="customer" id={customer.id} />
        </div>
        <CustomerButtons customer={customer} refresh={fetchCustomer} />
      </div>
    </div>
  );
}
